import {
  bugReportProblem,
  type BugReportContext,
  type BugReportGateway,
  type BugReportInput,
  type BugReportRecord,
  type BugStatus,
  type OwnBugReport,
} from './bugReports';

/**
 * Fehlermeldungen im Arbeitsspeicher — für den Demobetrieb.
 *
 * Bewusst mit denselben Prüfungen wie die Datenbank: wer FORQ ohne Server
 * ausprobiert, soll dasselbe Verhalten sehen. Eine Demo, die alles annimmt,
 * lehrt das Falsche.
 *
 * Was sie NICHT nachbaut, ist die Rechteprüfung — im Demobetrieb gibt es
 * keine Anmeldung, also auch niemanden, dem man etwas verweigern könnte.
 * Deshalb sieht `listAll` hier dasselbe wie `listMine`.
 */
export class LocalBugReportGateway implements BugReportGateway {
  private readonly byTenant = new Map<string, BugReportRecord[]>();

  private liste(tenantId: string): BugReportRecord[] {
    const vorhanden = this.byTenant.get(tenantId);
    if (vorhanden) return vorhanden;
    const neu: BugReportRecord[] = [];
    this.byTenant.set(tenantId, neu);
    return neu;
  }

  async report(tenantId: string, input: BugReportInput, context: BugReportContext): Promise<void> {
    const problem = bugReportProblem(input);
    if (problem) throw new Error(problem);

    this.liste(tenantId).unshift({
      id: `demo-bug-${Date.now()}-${this.liste(tenantId).length + 1}`,
      title: input.title.trim(),
      description: input.description.trim(),
      severity: input.severity,
      status: 'neu',
      route: context.route,
      resolutionNote: '',
      createdAt: new Date().toISOString(),
      handledAt: null,
      reportedByName: 'Demo-Benutzer',
      handledByName: null,
      appVersion: context.appVersion,
      userAgent: context.userAgent,
      viewport: context.viewport,
    });
  }

  async listMine(tenantId: string): Promise<OwnBugReport[]> {
    return this.liste(tenantId).map((eintrag) => ({ ...eintrag }));
  }

  async listAll(tenantId: string): Promise<BugReportRecord[]> {
    return this.liste(tenantId).map((eintrag) => ({ ...eintrag }));
  }

  async setStatus(
    tenantId: string,
    reportId: string,
    status: BugStatus,
    note: string,
  ): Promise<BugReportRecord[]> {
    // Dieselbe Regel wie in der Datenbank: "kein Fehler" ist für den Melder
    // eine Tür, die zugeht. Wer so entscheidet, schreibt einen Satz dazu.
    if (status === 'kein_fehler' && note.trim().length < 3) {
      throw new Error('Bitte begründen, warum das kein Fehler ist.');
    }

    const liste = this.liste(tenantId);
    const index = liste.findIndex((eintrag) => eintrag.id === reportId);
    if (index < 0) throw new Error('Diese Fehlermeldung gibt es in diesem Mandanten nicht.');

    liste[index] = {
      ...liste[index],
      status,
      resolutionNote: note.trim().slice(0, 2000),
      handledByName: 'Demo-Benutzer',
      handledAt: new Date().toISOString(),
    };
    return liste.map((eintrag) => ({ ...eintrag }));
  }
}

export function createLocalBugReportGateway(): BugReportGateway {
  return new LocalBugReportGateway();
}
